import { useCallback, useEffect, useState } from "react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

import {
  Fuel,
  Truck,
  Plus,
  ArrowRightLeft,
  ArrowUpFromLine,
  ArrowDownToLine,
  Hash,
  RefreshCw,
} from "lucide-react";

import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmtL = (value) =>
  `${Number(value || 0).toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
  })} L`;

const MOVEMENT_ICON = {
  adjust: ArrowDownToLine,
  transfer: ArrowRightLeft,
  refuel: ArrowUpFromLine,
};

const MOVEMENT_LABEL = {
  adjust: "Ajuste / NF",
  transfer: "Transferência",
  refuel: "Abastecimento",
  reverse: "Estorno",
};

export default function FuelStock() {
  const [tanks, setTanks] = useState([]);
  const [movements, setMovements] = useState([]);

  const [open, setOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const [tankId, setTankId] = useState("");
  const [volume, setVolume] = useState("");
  const [notes, setNotes] = useState("NF de entrada");

  const { user } = useAuth();

  const canManageFuel =
    user?.role === "admin" ||
    user?.role === "gestor";

  const load = useCallback(async () => {
    try {
      const [tankResponse, movementResponse] = await Promise.all([
        api.get("/fuel/tanks"),
        api.get("/fuel/movements"),
      ]);

      const tankData = tankResponse.data || [];
      const movementData = movementResponse.data || [];

      setTanks(tankData);
      setMovements(movementData);

      setTankId((currentTankId) => {
        if (currentTankId) {
          return currentTankId;
        }

        const stationary = tankData.find(
          (tank) => tank.kind === "stationary"
        );

        return stationary?.id || "";
      });
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail ||
            "Não foi possível carregar os dados de combustível."
        )
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const recompute = async () => {
    setRecomputing(true);

    try {
      const { data } = await api.post("/fuel/recompute");

      const changed = (data.tanks || []).filter(
        (tank) =>
          Math.abs(
            Number(tank.previous || 0) -
              Number(tank.recomputed || 0)
          ) > 0.01
      );

      toast.success(
        `Saldos recalculados · ${changed.length} tanque(s) ajustado(s)`
      );

      await load();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
      );
    } finally {
      setRecomputing(false);
    }
  };

  const adjust = async () => {
    const parsedVolume = Number(volume);

    if (!tankId || !Number.isFinite(parsedVolume) || parsedVolume === 0) {
      toast.error("Preencha tanque e volume");
      return;
    }

    try {
      await api.post("/fuel/adjust", {
        tank_id: tankId,
        volume: parsedVolume,
        notes,
      });

      toast.success("Ajuste registrado");

      setOpen(false);
      setVolume("");

      await load();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail)
      );
    }
  };

  return (
    <div
      className="p-6 max-w-7xl mx-auto"
      data-testid="fuel-page"
    >
      <PageHeader
        title="Estoque de Combustível"
        subtitle="Controle de tanque estacionário e caminhão comboio"
        actions={
          canManageFuel ? (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={recompute}
                disabled={recomputing}
                className="gap-2"
                data-testid="recompute-btn"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    recomputing ? "animate-spin" : ""
                  }`}
                />

                {recomputing
                  ? "Recalculando..."
                  : "Recalcular Saldos"}
              </Button>

              <Dialog
                open={open}
                onOpenChange={setOpen}
              >
                <DialogTrigger asChild>
                  <Button
                    className="gap-2"
                    data-testid="fuel-adjust-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Ajuste / Entrada de NF
                  </Button>
                </DialogTrigger>

                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      Ajuste / Entrada de Combustível
                    </DialogTitle>
                  </DialogHeader>

                  <div className="space-y-3">
                    <div>
                      <Label>Tanque</Label>

                      <Select
                        value={tankId}
                        onValueChange={setTankId}
                      >
                        <SelectTrigger data-testid="adjust-tank">
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          {tanks.map((tank) => (
                            <SelectItem
                              key={tank.id}
                              value={tank.id}
                            >
                              {tank.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Volume (L)</Label>

                      <Input
                        type="number"
                        placeholder="Ex: 2000 (entrada) ou -100 (saída)"
                        value={volume}
                        onChange={(event) =>
                          setVolume(event.target.value)
                        }
                        data-testid="adjust-volume"
                      />

                      <p className="text-xs text-muted-foreground mt-1">
                        Valores positivos entram, negativos saem.
                      </p>
                    </div>

                    <div>
                      <Label>Observação</Label>

                      <Textarea
                        rows={2}
                        value={notes}
                        onChange={(event) =>
                          setNotes(event.target.value)
                        }
                        data-testid="adjust-notes"
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      onClick={adjust}
                      data-testid="save-adjust"
                    >
                      Registrar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          ) : null
        }
      />

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {tanks.map((tank) => {
          const capacity = Number(tank.capacity_liters || 0);
          const current = Number(tank.current_liters || 0);

          const pct =
            capacity > 0
              ? Math.min(
                  100,
                  Math.max(0, (current / capacity) * 100)
                )
              : 0;

          const Icon =
            tank.kind === "stationary"
              ? Fuel
              : Truck;

          const barColor =
            pct < 20
              ? "text-red-500"
              : pct < 40
              ? "text-amber-500"
              : "text-emerald-500";

          return (
            <Card
              key={tank.id}
              className="p-6 border-border"
              data-testid={`tank-${tank.kind}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>

                  <div>
                    <div className="font-display font-extrabold text-xl uppercase tracking-tight">
                      {tank.name}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Capacidade: {fmtL(capacity)}
                    </div>
                  </div>
                </div>

                <Badge
                  variant="outline"
                  className={`font-mono text-xs ${barColor}`}
                >
                  {pct.toFixed(0)}%
                </Badge>
              </div>

              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  Saldo Atual Restante
                </div>

                <div
                  className="font-display font-extrabold text-4xl sm:text-5xl font-mono leading-none mt-1"
                  data-testid={`tank-${tank.kind}-current`}
                >
                  {fmtL(current)}
                </div>
              </div>

              <Progress
                value={pct}
                className="h-3"
              />
            </Card>
          );
        })}
      </div>

      <Card className="p-0 overflow-x-auto">
        <div className="px-5 py-4 border-b border-border">
          <div className="font-display font-bold text-lg">
            Histórico de Movimentações
          </div>

          <div className="text-xs text-muted-foreground">
            Transferências, abastecimentos e ajustes manuais
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>
                Destino / Equipamento
              </TableHead>
              <TableHead>Abastecedor</TableHead>
              <TableHead>Recebedor</TableHead>
              <TableHead className="text-right">
                Volume
              </TableHead>
              <TableHead>Leitura</TableHead>
              <TableHead>Protocolo</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {movements.map((movement) => {
              const Icon =
                MOVEMENT_ICON[movement.type] ||
                ArrowRightLeft;

              return (
                <TableRow
                  key={movement.id}
                  data-testid={`mov-${movement.id}`}
                >
                  <TableCell className="text-xs font-mono">
                    {movement.timestamp
                      ? new Date(
                          movement.timestamp
                        ).toLocaleString("pt-BR")
                      : "—"}
                  </TableCell>

                  <TableCell>
                    <span className="inline-flex items-center gap-1">
                      <Icon className="w-3.5 h-3.5 text-primary" />

                      {MOVEMENT_LABEL[movement.type] ||
                        movement.type}
                    </span>
                  </TableCell>

                  <TableCell className="text-xs">
                    {movement.source_tank?.name || "—"}
                  </TableCell>

                  <TableCell className="text-xs">
                    {movement.dest_tank?.name ||
                      (movement.vehicle
                        ? `${movement.vehicle.fleet_number} · ${movement.vehicle.plate}`
                        : "—")}
                  </TableCell>

                  <TableCell className="text-xs">
                    {movement.operator?.name || "—"}
                  </TableCell>

                  <TableCell className="text-xs">
                    {movement.receiver?.name || "—"}
                  </TableCell>

                  <TableCell className="text-right font-mono font-bold">
                    {fmtL(movement.volume)}
                  </TableCell>

                  <TableCell className="text-xs font-mono">
                    {movement.reading_value != null
                      ? `${Number(
                          movement.reading_value
                        ).toLocaleString("pt-BR")} ${
                          movement.reading_type ===
                          "hours"
                            ? "h"
                            : "km"
                        }`
                      : "—"}
                  </TableCell>

                  <TableCell>
                    {movement.task?.protocol_number ? (
                      <a
                        href={`/inbox/${movement.task.id}`}
                        className="text-primary font-mono text-xs hover:underline flex items-center gap-1"
                      >
                        <Hash className="w-3 h-3" />

                        {
                          movement.task
                            .protocol_number
                        }
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {movements.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground py-8"
                >
                  Nenhuma movimentação registrada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}